import { useMemo, useState, useEffect } from "react";
import { createFileRoute, Navigate } from "@tanstack/react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ShieldCheck, Save } from "lucide-react";
import { AppHeader } from "@/components/app/AppHeader";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { useAllProfiles, useAllRoles } from "@/hooks/use-admin-data";
import { useAuth, DEPARTMENT_LABELS, type Department } from "@/lib/auth-context";
import { DEPARTMENT_CONFIGS } from "@/lib/department-configs";
import {
  DEPT_CAPS,
  DEPT_CAP_LABELS,
  DEPT_CAP_DESCRIPTIONS,
  WORKSPACE_CAPS,
  WORKSPACE_CAP_LABELS,
  WORKSPACE_CAP_DESCRIPTIONS,
  USER_MGMT_CAPS,
  USER_MGMT_CAP_LABELS,
  LOOKUP_CAPS,
  LOOKUP_CAP_LABELS,
  emptyPermissions,
  type DeptCap,
  type WorkspaceCap,
  type UserMgmtCap,
  type LookupCap,
  type ModeratorPermissions,
} from "@/lib/moderator-permissions";
import { updateModeratorPermissions } from "@/lib/rpc/admin.functions";

const DEPTS = Object.keys(DEPARTMENT_CONFIGS) as Department[];

export const Route = createFileRoute("/_app/_admin/admin/moderators")({
  component: () => <Navigate to="/admin/settings" search={{ tab: "moderators" } as never} />,
});

function totalAvailable() {
  return (
    WORKSPACE_CAPS.length +
    DEPTS.length * DEPT_CAPS.length +
    USER_MGMT_CAPS.length +
    LOOKUP_CAPS.length
  );
}

function countEnabled(p: ModeratorPermissions): number {
  let n = 0;
  for (const c of WORKSPACE_CAPS) if (p[c]) n++;
  for (const d of DEPTS) for (const c of DEPT_CAPS) if (p.departments?.[d]?.[c]) n++;
  for (const c of USER_MGMT_CAPS) if (p.user_management?.[c]) n++;
  for (const c of LOOKUP_CAPS) if (p.globals?.[c]) n++;
  return n;
}

export function ModeratorsPage() {
  const { role } = useAuth();
  const isAdmin = role === "admin";
  const { data: profiles = [] } = useAllProfiles();
  const { data: roles = [] } = useAllRoles();

  const moderators = useMemo(() => {
    const modIds = new Set(roles.filter((r) => r.role === "moderator").map((r) => r.user_id));
    return profiles.filter((p) => modIds.has(p.id));
  }, [profiles, roles]);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  useEffect(() => {
    if (!selectedId && moderators.length > 0) setSelectedId(moderators[0].id);
  }, [moderators, selectedId]);

  const selected = moderators.find((m) => m.id === selectedId) ?? null;
  const total = totalAvailable();

  if (!isAdmin) {
    return (
      <>
        <AppHeader subtitle="Moderator permissions" />
        <div className="px-[22px] pb-6 text-[12px] text-muted-foreground">Admin access required.</div>
      </>
    );
  }

  return (
    <>
      <AppHeader subtitle="Grant cross-department visibility & capabilities to moderators" />
      <div className="grid gap-4 px-[22px] pb-6 lg:grid-cols-[300px_1fr]">
        <aside className="rounded-lg border border-border bg-card p-3 shadow-sm">
          <div className="mb-2 flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.4px] text-muted-foreground">
            <ShieldCheck className="h-3 w-3" /> Moderators ({moderators.length})
          </div>
          {moderators.length === 0 && (
            <div className="rounded-md border border-dashed border-border p-3 text-[11px] text-muted-foreground">
              No moderators yet. Promote a user from the Users tab.
            </div>
          )}
          <ul className="flex flex-col gap-1">
            {moderators.map((m) => {
              const active = m.id === selectedId;
              const perms = ((m as unknown as { moderator_permissions?: ModeratorPermissions })
                .moderator_permissions) ?? {};
              const enabled = countEnabled(perms);
              return (
                <li key={m.id}>
                  <button
                    onClick={() => setSelectedId(m.id)}
                    className={`flex w-full flex-col items-start rounded-md px-2 py-2 text-left text-[12px] transition-colors ${
                      active ? "bg-primary text-primary-foreground" : "hover:bg-muted"
                    }`}
                  >
                    <span className="font-medium">{m.full_name}</span>
                    <span className={`text-[10px] ${active ? "text-primary-foreground/70" : "text-muted-foreground"}`}>
                      {DEPARTMENT_LABELS[m.department]} · {enabled} of {total} permissions
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </aside>

        {selected ? (
          <PermissionsEditor
            key={selected.id}
            userId={selected.id}
            initial={
              ((selected as unknown as { moderator_permissions?: ModeratorPermissions })
                .moderator_permissions) ?? emptyPermissions()
            }
          />
        ) : (
          <div className="rounded-lg border border-border bg-card p-6 text-[12px] text-muted-foreground shadow-sm">
            Select a moderator to manage their permissions.
          </div>
        )}
      </div>
    </>
  );
}

function PermissionsEditor({
  userId,
  initial,
}: {
  userId: string;
  initial: ModeratorPermissions;
}) {
  const qc = useQueryClient();
  const [perms, setPerms] = useState<ModeratorPermissions>(() => ({
    ...emptyPermissions(),
    ...initial,
    departments: { ...(initial.departments ?? {}) },
    user_management: { ...(initial.user_management ?? {}) },
    lookups: { ...(initial.lookups ?? {}) },
  }));

  const mut = useMutation({
    mutationFn: () => updateModeratorPermissions({ data: { user_id: userId, permissions: perms } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin"] });
      toast.success("Permissions saved");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const setWorkspace = (cap: WorkspaceCap, val: boolean) =>
    setPerms((p) => ({ ...p, [cap]: val }));
  const setDeptCap = (dept: Department, cap: DeptCap, val: boolean) =>
    setPerms((p) => ({
      ...p,
      departments: {
        ...p.departments,
        [dept]: { ...(p.departments?.[dept] ?? {}), [cap]: val },
      },
    }));
  const setUserMgmt = (cap: UserMgmtCap, val: boolean) =>
    setPerms((p) => ({ ...p, user_management: { ...(p.user_management ?? {}), [cap]: val } }));
  const setLookup = (cap: LookupCap, val: boolean) =>
    setPerms((p) => ({ ...p, globals: { ...(p.globals ?? {}), [cap]: val } }));

  const enableAll = () => {
    const all: ModeratorPermissions = { ...emptyPermissions() };
    for (const c of WORKSPACE_CAPS) all[c] = true;
    all.departments = Object.fromEntries(
      DEPTS.map((d) => [d, Object.fromEntries(DEPT_CAPS.map((c) => [c, true]))]),
    ) as ModeratorPermissions["departments"];
    all.user_management = Object.fromEntries(USER_MGMT_CAPS.map((c) => [c, true]));
    all.globals = { ...(all.globals ?? {}), ...Object.fromEntries(LOOKUP_CAPS.map((c) => [c, true])) };
    setPerms(all);
  };

  const disableAll = () => setPerms(emptyPermissions());

  return (
    <div className="rounded-lg border border-border bg-card shadow-sm">
      <div className="flex flex-col gap-3 border-b border-border p-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.4px] text-muted-foreground">
            Permissions
          </div>
          <h2 className="text-[15px] font-bold">Moderator capabilities</h2>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={enableAll}>Enable all</Button>
          <Button size="sm" variant="outline" onClick={disableAll}>Disable all</Button>
          <Button size="sm" disabled={mut.isPending} onClick={() => mut.mutate()}>
            <Save className="mr-1 h-3.5 w-3.5" />
            {mut.isPending ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>

      {/* Workspace-level */}
      <Section title="Workspace-level access">
        <div className="grid gap-2 sm:grid-cols-2">
          {WORKSPACE_CAPS.map((cap) => (
            <ToggleRow
              key={cap}
              label={WORKSPACE_CAP_LABELS[cap]}
              description={WORKSPACE_CAP_DESCRIPTIONS[cap]}
              checked={Boolean(perms[cap])}
              onChange={(v) => setWorkspace(cap, v)}
            />
          ))}
        </div>
      </Section>

      {/* Per-department */}
      <Section title="Per-department access">
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]">
            <thead className="bg-muted/40">
              <tr>
                <th className="px-3 py-2 text-left font-semibold">Department</th>
                {DEPT_CAPS.map((c) => (
                  <th key={c} className="px-2 py-2 text-center font-semibold" title={DEPT_CAP_DESCRIPTIONS[c]}>
                    {DEPT_CAP_LABELS[c]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {DEPTS.map((dept) => (
                <tr key={dept} className="border-t border-border">
                  <td className="px-3 py-2 font-medium">{DEPARTMENT_LABELS[dept]}</td>
                  {DEPT_CAPS.map((cap) => (
                    <td key={cap} className="px-2 py-2 text-center">
                      <Switch
                        checked={Boolean(perms.departments?.[dept]?.[cap])}
                        onCheckedChange={(v) => setDeptCap(dept, cap, Boolean(v))}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {/* User management */}
      <Section title="User management">
        <div className="grid gap-2 sm:grid-cols-2">
          {USER_MGMT_CAPS.map((cap) => (
            <ToggleRow
              key={cap}
              label={USER_MGMT_CAP_LABELS[cap]}
              description=""
              checked={Boolean(perms.user_management?.[cap])}
              onChange={(v) => setUserMgmt(cap, v)}
            />
          ))}
        </div>
      </Section>

      {/* Lookups */}
      <Section title="Lookups">
        <div className="grid gap-2 sm:grid-cols-2">
          {LOOKUP_CAPS.map((cap) => (
            <ToggleRow
              key={cap}
              label={LOOKUP_CAP_LABELS[cap]}
              description="Edit values in /admin/settings/lookups"
              checked={Boolean(perms.globals?.[cap])}
              onChange={(v) => setLookup(cap, v)}
            />
          ))}
        </div>
      </Section>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="border-t border-border p-4">
      <div className="mb-3 text-[10px] font-semibold uppercase tracking-[0.4px] text-muted-foreground">
        {title}
      </div>
      {children}
    </div>
  );
}

function ToggleRow({
  label,
  description,
  checked,
  onChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-3 rounded-md border border-border bg-background px-3 py-2">
      <Switch checked={checked} onCheckedChange={(v) => onChange(Boolean(v))} className="mt-0.5" />
      <div className="min-w-0">
        <div className="text-[12px] font-medium">{label}</div>
        {description && <div className="text-[10px] text-muted-foreground">{description}</div>}
      </div>
    </label>
  );
}
